/*
 * Wizard Step 2 (U7 — migrated to Mantine): pull live Notion tickets into a
 * validated grid.
 *
 *   - `refetch` prop wires the "Refresh" button (Refresh-all) to the existing
 *     preview query's `refetch`. While a Refresh-all is in flight, the grid dims
 *     in place (no full-screen EmptyState — that would drop the visible selection).
 *   - `onRefreshSelected` prop wires the "Refresh selected" button to the
 *     `useRefreshSelectedTickets` mutation passed down from the wizard. Disabled
 *     when nothing is selected.
 *   - `isRefetchingAll` / `isRefreshingSelected` control spinner visibility on
 *     each button.
 *   - When a refreshed row comes back `already_queued:true` for a ticket that was
 *     in `selected`, the wizard drops it from the selection (Step2 doesn't own the
 *     selection set — LaunchWizard does). The `TicketGrid` shows the inline
 *     queued-note on those rows via the `already_queued` field.
 *   - `refreshingIds` threads down to TicketGrid for per-row spinners during
 *     Refresh-selected.
 *
 * Presentation contract:
 *   - isLoading   → full-screen "Loading tickets…" EmptyState (first load only,
 *                   before any data). Not shown during Refresh-all re-pulls (those
 *                   use the dimmed-grid overlay so the selection stays visible).
 *   - isError     → actionable error alert with Retry.
 *   - data        → button row + count line + TicketGrid.
 */

import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import { EmptyState } from "../ui";
import { errorMessage } from "../../api/errors";
import type { PreviewTicketsResponse } from "../../api/client";
import { TicketGrid } from "./TicketGrid";

export function Step2SelectTickets({
  isLoading,
  isError,
  error,
  data,
  flow,
  format,
  selected,
  onToggle,
  onToggleAll,
  onRetry,
  refetch,
  isRefetchingAll,
  onRefreshSelected,
  isRefreshingSelected,
  refreshingIds,
  copyTabPicks,
  onPickCopyTab,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: PreviewTicketsResponse | undefined;
  /** Active Step-1 flow filter (for the "none match" empty-state copy). */
  flow?: string;
  /** Active Step-1 format filter (for the "none match" empty-state copy). */
  format?: string;
  selected: Set<string>;
  onToggle: (pageId: string, checked: boolean) => void;
  onToggleAll: (validPageIds: string[], checked: boolean) => void;
  onRetry: () => void;
  /** Calls the preview query's `refetch` (Refresh-all). Optional — when absent the Refresh button is hidden. */
  refetch?: () => void;
  /** True while the Refresh-all re-pull is in flight. */
  isRefetchingAll?: boolean;
  /** Calls `useRefreshSelectedTickets` mutation for the current selection. Optional — when absent the Refresh selected button is hidden. */
  onRefreshSelected?: (pageIds: string[]) => void;
  /** True while the Refresh-selected mutation is pending. */
  isRefreshingSelected?: boolean;
  /** Page ids whose rows are being individually refreshed (per-row spinners). */
  refreshingIds?: Set<string>;
  /** 2026-07-01-002: pageId → chosen copy sub-tab id (empty until picked). */
  copyTabPicks?: Record<string, string>;
  /** Set the chosen copy sub-tab for a ticket whose linked copy tab is empty. */
  onPickCopyTab?: (pageId: string, tabId: string | null) => void;
}) {
  if (isLoading) {
    return <EmptyState icon="◷" title="Loading tickets…" />;
  }

  if (isError) {
    return (
      <Alert color="red" variant="light" role="alert">
        Couldn't load tickets from Notion: {errorMessage(error)}. Check the
        brand's Notion credential and data source, then{" "}
        <Button variant="subtle" size="compact-sm" onClick={onRetry}>
          Retry
        </Button>
        .
      </Alert>
    );
  }

  if (!data) return null;

  const { tickets, total_ready_to_launch } = data;

  if (tickets.length === 0) {
    if (total_ready_to_launch === 0) {
      return (
        <EmptyState
          icon="▦"
          title="None Ready to Launch"
          description="This product has no tickets in the Ready to Launch status."
        />
      );
    }
    // Ready tickets exist but the Flow/Format filter excluded them all — name the
    // active filter(s) so the operator knows what to clear on the previous step.
    const activeFlow = flow?.trim();
    const activeFormat = format?.trim();
    const parts = [
      activeFlow ? `flow "${activeFlow}"` : null,
      activeFormat ? `format "${activeFormat}"` : null,
    ].filter(Boolean) as string[];
    const noMatchTitle =
      activeFlow && !activeFormat
        ? "None match this flow"
        : activeFormat && !activeFlow
          ? "None match this format"
          : "None match the filters";
    return (
      <EmptyState
        icon="▦"
        title={noMatchTitle}
        description={`There are ${total_ready_to_launch} Ready-to-Launch ticket(s), but none match ${parts.join(" and ") || "the active filters"}. Clear or change the filters on the previous step.`}
      />
    );
  }

  /** Rows that are selectable: valid, not already queued, and not awaiting a copy-tab pick. */
  const selectableIds = tickets
    .filter(
      (t) =>
        t.is_valid &&
        !t.already_queued &&
        !(t.needs_copy_tab_pick && !(copyTabPicks ?? {})[t.page_id]),
    )
    .map((t) => t.page_id);

  const selectedIds = Array.from(selected);
  const hasSelection = selectedIds.length > 0;

  const showRefreshRow = refetch !== undefined || onRefreshSelected !== undefined;

  return (
    <Stack gap="sm">
      {/* ---- Refresh button row (shown when at least one refresh prop provided) -- */}
      {showRefreshRow && (
        <Group gap="sm">
          {refetch !== undefined && (
            <Button
              variant="default"
              size="xs"
              onClick={refetch}
              disabled={(isRefetchingAll ?? false) || (isRefreshingSelected ?? false)}
              aria-label="Refresh all tickets"
              leftSection={
                (isRefetchingAll ?? false) ? <Loader size={12} /> : undefined
              }
            >
              Refresh
            </Button>
          )}
          {onRefreshSelected !== undefined && (
            <Button
              variant="default"
              size="xs"
              onClick={() => onRefreshSelected(selectedIds)}
              disabled={
                !hasSelection ||
                (isRefetchingAll ?? false) ||
                (isRefreshingSelected ?? false)
              }
              aria-label="Refresh selected tickets"
              leftSection={
                (isRefreshingSelected ?? false) ? <Loader size={12} /> : undefined
              }
            >
              Refresh selected
            </Button>
          )}
        </Group>
      )}

      <Text c="dimmed" fz="sm">
        {selectableIds.length} of {tickets.length} ticket(s) selectable. Invalid
        rows show the reason inline.
      </Text>

      {/* ---- Grid — dims but stays mounted during Refresh-all ----------- */}
      <TicketGrid
        tickets={tickets}
        selected={selected}
        onToggle={onToggle}
        onToggleAll={(checked) => onToggleAll(selectableIds, checked)}
        isRefreshing={isRefetchingAll ?? false}
        refreshingIds={refreshingIds}
        copyTabPicks={copyTabPicks}
        onPickCopyTab={onPickCopyTab}
      />
    </Stack>
  );
}
